import { compareSgsProductionCalendarStationOrder } from "@/lib/sgsProductionCalendarStationOrder";

/** Shape from GET `/api/manufacturing/alternates` — only fields used on the production calendar. */
export interface ManufacturingAlternatesBoardApiResponse {
  groups: ManufacturingAlternatesBoardApiGroup[];
}

export interface ManufacturingAlternatesBoardApiGroup {
  devices: Array<{ stationId: number | null; stationName: string | null }>;
  eligibleBatchItems: Array<{ batchId: number }>;
}

export interface AlternateBoardAvailabilityStationRow {
  stationId: number | null;
  stationName: string;
  count: number;
}

/** Alternate units on the board whose SKU group lists this batch as an eligible target. */
export interface AlternateBoardAvailability {
  alternateCount: number;
  byStation: AlternateBoardAvailabilityStationRow[];
}

function alternateStationBucketKey(stationId: number | null, stationName: string): string {
  return stationId != null ? `id:${stationId}` : `name:${stationName}`;
}

/**
 * Groups alternate devices by current station when at least one eligible batch item matches `batchId`.
 * Mirrors alternates board eligibility (comparison-key batch lines), not calendar WIP stations.
 */
export function alternateBoardAvailabilityForBatch(
  groups: ReadonlyArray<{
    devices: ReadonlyArray<{ stationId: number | null; stationName: string | null }>;
    eligibleBatchItems: ReadonlyArray<{ batchId: number }>;
  }>,
  batchId: number,
): AlternateBoardAvailability | null {
  const stationMap = new Map<
    string,
    { stationId: number | null; stationName: string; count: number }
  >();
  let alternateCount = 0;

  for (const group of groups) {
    if (!group.eligibleBatchItems.some((item) => item.batchId === batchId)) continue;
    for (const device of group.devices) {
      const stationId = device.stationId ?? null;
      const stationName =
        device.stationName == null || device.stationName.trim() === ""
          ? "No station"
          : device.stationName.trim();
      const key = alternateStationBucketKey(stationId, stationName);
      const existing = stationMap.get(key);
      if (existing != null) {
        existing.count += 1;
      } else {
        stationMap.set(key, { stationId, stationName, count: 1 });
      }
      alternateCount += 1;
    }
  }

  if (alternateCount === 0) return null;

  const byStation = [...stationMap.values()].sort((left, right) =>
    compareSgsProductionCalendarStationOrder(
      { stationId: left.stationId, isTerminal: false, deviceCount: left.count },
      { stationId: right.stationId, isTerminal: false, deviceCount: right.count },
    ),
  );

  return { alternateCount, byStation };
}
